import { Component, Prop, State, h, Element } from '@stencil/core';
import { PageNode } from 'interfaces/PageNode';


@Component({
    tag: 'app-nav',
    styleUrl: 'app-nav.scss',
    shadow: false,
})
export class AppNav {
    @Element() root: HTMLElement
    @State() isMobile: boolean = true;
    @Prop() pageNodes: string = '';
    @Prop() drupalNodes: string = '';
    @Prop() allItems: string = '';
    pageNodeArray: Array<PageNode> = [];

    componentWillLoad() {
        if (this.drupalNodes !== '') {
            this.pageNodeArray = JSON.parse(this.drupalNodes.trim());
        }
    }

    componentDidRender() {
        this.toggleMobileNav();

        const mediaQuery = window.matchMedia('(max-width: 1199.98px)');
        mediaQuery.addEventListener('change', (event) => {
            const navToggle: HTMLElement = document.querySelector('.mobile-nav-toggle');
            if (!event.matches) {
                // Screen width is at least 1199.98
                //removing the dark overlay in case nav is open when going from mobile to desktop
                document.querySelector('body').classList.remove('menu-entered');
            } else {
                if (navToggle.classList.contains('open')) {
                    //adding the dark overlay in case nav was open on mobile, moved to dekstop and then came back again to mobile
                    document.querySelector('body').classList.add('menu-entered');
                }
            }
        });

        //Handle tab keypress on the last menu item
        const lastMainMenuItem = this.root.querySelector('.node.last');
        const anchorTags = lastMainMenuItem.querySelectorAll('a');
        const lastAnchorTag = anchorTags.length > 0 ? anchorTags[anchorTags.length - 1] : null;

        if (lastAnchorTag) {
            lastAnchorTag.addEventListener('keydown', event => {
                if (!event.shiftKey && event.key.toLowerCase() === 'tab') {
                    this.menuEntered('leave')
                }
            })
        }
    }

    createChildrenMarkup(children: Array<PageNode>) {
        let output = '';

        children.forEach((child, index) => {

            if (index % 2 === 0) {
                output += '<div class="col-12 col-xl-6 children-wrapper">';
            }

            output += `<div class="child">
                    <a href="${child.UrlName}" aria-label="${child.Title}">
                        ${child.Title} <svg>
                            <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#caret-right"></use>
                        </svg>
                    </a>
                `;

            if (child.Nodes && child.Nodes.length) {
                output += '<div class="third-level">';

                child.Nodes.forEach((thirdLevel) => {
                    output += `<div>
                        <a href="${thirdLevel.UrlName}" aria-label="${thirdLevel.Title}">${thirdLevel.Title}</a>
                        </div>`;
                });

                output += '</div></div>';
            }

            if (index % 2 !== 0) {
                output += '</div>';
            }
        });

        return output;
    }

    createSimpleChildrenMarkup(children: Array<PageNode>) {
        const columns = [[], [], []];
        let icon = '';
        let desc = '';
        // Distribute children into columns
        children.forEach((child, index) => {
            let colIndex = index % 3;
            icon = '';
            desc = '';
            if (child.SubmenuIconName) {
                icon = `<div class="icon-wrapper">
                            <i class= "fa-solid ${child.SubmenuIconName}"></i>
                        </div>`;
            }
            if (child.SubmenuDescription) {
                desc = `<div class="description">${child.SubmenuDescription}</div>`;
            }
            columns[colIndex].push(`
                <a href="${child.UrlName}" aria-label="${child.Title}">
                    ${icon}
                    <div class="text-wrapper">
                        <div class="title">${child.Title} <svg>
                            <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#caret-right"></use></svg>
                        </div>
                        ${desc}
                    </div>
                </a>
            `);
        });
        // Wrap each column's content in a .children-wrapper div
        let output = '';
        columns.forEach((col) => {
            output += `<div class="col-12 col-xl-4 children-wrapper">${col.join('')}</div>`;
        });
        return output;
    }

    hasThirdLevel(children: Array<PageNode>) {
        var thirdLevels = children.filter((child) => {
            return child.Nodes && child.Nodes.length;
        });

        return thirdLevels.length > 0;
    }

    menuEntered(state) {
        if (window.innerWidth > 1199.98) {
            if (state == 'enter') {
                document.querySelector('body').classList.add('menu-entered');
            } else {
                document.querySelector('body').classList.remove('menu-entered');
            }
        }
    }

    mobileNavToggle(level: string, nodeIndex: number, childIndex: number = null) {
        const levels = {
            '1': '.node',
            '2': '.child'
        };

        let target = null;

        if (level == '1') {
            target = document.querySelectorAll('.node')[nodeIndex];
        } else if (level == '2') {
            target = document.querySelectorAll('.node')[nodeIndex].querySelector('.children-mobile').querySelectorAll('.child')[childIndex];
        }

        if (target) {
            target.classList.add('changing');

            document.querySelectorAll(levels[level]).forEach((node: HTMLElement) => {
                if (!node.classList.contains('changing')) {
                    node.classList.remove('open');
                }
            });

            target.classList.toggle('open');
            target.classList.remove('changing');
        }
    }

    calculateMobileNavPositions() {
        const nav: HTMLElement = document.querySelector('.app-nav');
        const utils: HTMLElement = document.querySelector('.util-nav-list');
        const header: HTMLElement = document.querySelector('.site-header');

        if (!nav || !utils || !header) {
            return;
        }

        const headerHeight = header.offsetHeight;
        const utilHeight = utils.offsetHeight;

        utils.style.top = headerHeight + 'px';
        nav.style.top = (headerHeight + utilHeight) + 'px';
        nav.style.maxHeight = `calc(100vh - ${headerHeight + utilHeight}px)`;
    }

    toggleMobileNav() {
        const toggle: HTMLElement = document.querySelector('.mobile-nav-toggle');
        const nav: HTMLElement = document.querySelector('.utility-main-nav-wrapper');

        if (!toggle || !nav) {
            return;
        }

        toggle.onclick = (e) => {
            e.preventDefault();
            //Close search if open
            const appSearch = document.querySelector('.app-site-search .search-overlay');
            appSearch.classList.remove('show')
            const searchToggle: HTMLElement = document.querySelector('.mobile-search-toggle');
            searchToggle.classList.remove('open');
            document.querySelector('body').classList.remove('search-open');

            nav.classList.toggle('open');
            toggle.classList.toggle('open');
            document.querySelector('body').classList.toggle('menu-entered');
            this.calculateMobileNavPositions();
        };

        window.addEventListener('resize', () => {
            if (document.querySelector('body').classList.contains('menu-entered')) {
                this.calculateMobileNavPositions();
            }

        });
    }

    onMainNavWithKeyboard(event: KeyboardEvent) {
        const target = event.target as Element;
        if (!event.shiftKey && event.key.toLowerCase() === 'tab') {
            this.menuEntered('enter')
        } else if (event.shiftKey && event.key.toLowerCase() === 'tab') {
            if (!target.closest('.node').previousElementSibling.classList.contains('node')) {
                this.menuEntered('leave')
            } else {
                this.menuEntered('enter')
            }
        }
    }

    onMouseEnterItem(showDarkOverlay: boolean) {
        if(showDarkOverlay) {
            this.menuEntered('enter')
        }
    }

    render() {
        return (
            <div class="app-nav">
                <div class="container contact-us mobile">
                    <a href={this.pageNodeArray[this.pageNodeArray.length - 1].UrlName} class="btn btn-primary">{this.pageNodeArray[this.pageNodeArray.length - 1].Title}</a>
                </div>
                {this.pageNodeArray.map((node, nodeIndex) => (
                    nodeIndex !== this.pageNodeArray.length - 1 &&
                    <div class={`node${nodeIndex === this.pageNodeArray.length - 2 ? ' last' : ''}`} onMouseEnter={() => { this.onMouseEnterItem(Boolean(node.Nodes && node.Nodes.length)) }} onMouseLeave={() => { this.menuEntered('leave') }}>
                        <div class="parent container">
                            <a href={node.UrlName} aria-label={node.Title} onKeyDown={(e) => this.onMainNavWithKeyboard(e)}>
                                {node.Title}
                                {Boolean(node.Nodes && node.Nodes.length) && (
                                <svg>
                                    <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#caret-down"></use>
                                </svg>
                                )}
                            </a>

                            {Boolean(node.Nodes && node.Nodes.length) && (
                                <div class="parent-toggle" role="button" tabIndex={0} aria-label="Toggle Navigation Item" onClick={() => { this.mobileNavToggle('1', nodeIndex) }}>
                                    <span> <svg>
                                        <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#plus"></use>
                                    </svg> </span>
                                    <span> <svg>
                                        <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#minus"></use>
                                    </svg> </span>
                                </div>
                            )}
                        </div>

                        {(Boolean(node.Nodes && node.Nodes.length) && this.hasThirdLevel(node.Nodes)) && (
                            <div class={`sub mega-children`}>
                                {(Boolean(node.BannerText) && node.BannerText.trim() !== '') && (
                                    <div class="banner-text">
                                        <div class='container'>{node.BannerText.trim()}</div>
                                    </div>
                                )}
                                <div class="nav-wrapper container">
                                    <div class="children container">
                                        <div class="row" innerHTML={this.createChildrenMarkup(node.Nodes)}>
                                        </div>
                                    </div>

                                    <div class="children-mobile">
                                        {node.Nodes.map((child, childIndex) => (
                                            <div class="child">
                                                <div class="child-link">
                                                    <a href={child.UrlName} aria-label={child.Title}>
                                                        {child.Title}
                                                    </a>

                                                    {Boolean(child.Nodes && child.Nodes.length) && (
                                                        <div class="parent-toggle" role="button" tabIndex={0} aria-label="Toggle Navigation Item" onClick={() => { this.mobileNavToggle('2', nodeIndex, childIndex) }}>
                                                            <span> <svg>
                                                                <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#plus"></use>
                                                            </svg> </span>
                                                            <span> <svg>
                                                                <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#minus"></use>
                                                            </svg> </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {Boolean(child.Nodes && child.Nodes.length) && (
                                                    <div class="third-level">
                                                        {child.Nodes.map((thirdLevel) => (
                                                            <a href={thirdLevel.UrlName} aria-label={thirdLevel.Title}>
                                                                {thirdLevel.Title}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {node.hasCTA === "On" && (
                                        <app-nav-cta CTA={node.CTA}></app-nav-cta>
                                    )}
                                </div>
                            </div>
                        )}

                        {(Boolean(node.Nodes && node.Nodes.length) && !this.hasThirdLevel(node.Nodes)) && (
                            <div class="sub simple-children">
                                {(Boolean(node.BannerText) && node.BannerText.trim() !== '') && (
                                    <div class="banner-text">
                                        <div class='container'>{node.BannerText.trim()}</div>
                                    </div>
                                )}
                                <div class="simple-children-cta__wrapper container">
                                    <div class="simple-children__links">
                                        <div class="row" innerHTML={this.createSimpleChildrenMarkup(node.Nodes)}>
                                        </div>
                                    </div>

                                    {node.hasCTA === "On" && (
                                        <app-nav-cta CTA={node.CTA}></app-nav-cta>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                <div class="contact-us">
                    <a href={this.pageNodeArray[this.pageNodeArray.length - 1].UrlName} class="btn btn-primary">{this.pageNodeArray[this.pageNodeArray.length - 1].Title}</a>
                </div>
            </div>
        );
    }
}